import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userActionSet } from "../../redux/actions/userActions";
import { useUsersHook } from "../useUsersHook";

const PROFILE_PREFS_KEY = "preferences";

export const useWorkspaceTabs = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user);
  const { updateUserById, getLoggedInUser } = useUsersHook();
  const pendingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  const tabs = useMemo(() => {
    const stored = user?.[PROFILE_PREFS_KEY]?.workspacePinnedTabs;
    return Array.isArray(stored) ? stored : [];
  }, [user]);

  const mutateTabs = useCallback(
    (mutationFn) => {
      if (!user?._id || pendingRef.current) return;
      pendingRef.current = true;
      setLoading(true);

      getLoggedInUser({
        userId: user._id,
        onSuccessFn: (freshUser) => {
          const freshPrefs = freshUser?.[PROFILE_PREFS_KEY] || {};
          const currentTabs = Array.isArray(freshPrefs.workspacePinnedTabs)
            ? freshPrefs.workspacePinnedTabs
            : [];

          const updatedTabs = mutationFn(currentTabs);

          updateUserById({
            userId: user._id,
            data: {
              [PROFILE_PREFS_KEY]: {
                ...freshPrefs,
                workspacePinnedTabs: updatedTabs,
              },
            },
            onSuccessFn: (savedUser) => {
              dispatch(userActionSet(savedUser));
              pendingRef.current = false;
              setLoading(false);
            },
            onErrorFn: (err) => {
              console.error("Failed to save pinned tabs:", err);
              pendingRef.current = false;
              setLoading(false);
            },
          });
        },
        onErrorFn: (err) => {
          console.error("Failed to fetch fresh user for tab mutation:", err);
          pendingRef.current = false;
          setLoading(false);
        },
      });
    },
    [user, getLoggedInUser, updateUserById, dispatch],
  );

  const addTab = useCallback(
    ({ _id, loanNumber, loanName }) => {
      mutateTabs((currentTabs) => {
        if (currentTabs.some((t) => t._id === _id)) return currentTabs;
        return [...currentTabs, { _id, loanNumber, loanName }];
      });
    },
    [mutateTabs],
  );

  const removeTab = useCallback(
    (loanNumber) => {
      mutateTabs((currentTabs) =>
        currentTabs.filter((t) => String(t.loanNumber) !== String(loanNumber)),
      );
    },
    [mutateTabs],
  );

  const isTabPinned = useCallback(
    (loanNumber) => {
      return tabs.some((t) => String(t.loanNumber) === String(loanNumber));
    },
    [tabs],
  );

  const toggleTab = useCallback(
    ({ _id, loanNumber, loanName }) => {
      if (isTabPinned(loanNumber)) {
        removeTab(loanNumber);
      } else {
        addTab({ _id, loanNumber, loanName });
      }
    },
    [isTabPinned, addTab, removeTab],
  );

  return {
    tabs,
    loading,
    addTab,
    removeTab,
    toggleTab,
    isTabPinned,
  };
};
